import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { Trash2, Plus, Pencil, Calendar as CalendarIcon, Clock, Coffee, ChevronUp } from 'lucide-react';

type Interval = { start: string; end: string };
type Day = { enabled: boolean; intervals: Interval[]; breaks: Interval[] };

const DAY_NAMES = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const WEEKDAY_INDICES = [0, 1, 2, 3, 4]; // א–ה
const FRIDAY = 5;
const SATURDAY = 6;

type Mode = 'unified' | 'per-day';

function emptyDay(): Day {
  return { enabled: true, intervals: [{ start: '09:00', end: '17:00' }], breaks: [] };
}

function closedDay(): Day {
  return { enabled: false, intervals: [], breaks: [] };
}

function sameDay(a: Day, b: Day): boolean {
  if (a.enabled !== b.enabled) return false;
  const sameArr = (x: Interval[], y: Interval[]) =>
    x.length === y.length && x.every((iv, i) => iv.start === y[i].start && iv.end === y[i].end);
  return sameArr(a.intervals, b.intervals) && sameArr(a.breaks, b.breaks);
}

function detectMode(weekly: Day[]): Mode {
  if (weekly.length !== 7) return 'per-day';
  const first = weekly[0];
  const allWeekdaysSame = WEEKDAY_INDICES.every((i) => sameDay(weekly[i], first));
  return allWeekdaysSame ? 'unified' : 'per-day';
}

export default function AdminCalendar() {
  const [id, setId] = useState<string | null>(null);
  const [weekly, setWeekly] = useState<Day[]>([]);
  const [mode, setMode] = useState<Mode>('unified');
  const [buffer, setBuffer] = useState(10);
  const [slotStep, setSlotStep] = useState(15);
  const [minLead, setMinLead] = useState(120);
  const [horizon, setHorizon] = useState(60);
  const [confirmationMessage, setConfirmationMessage] = useState<string>(
    'הבקשה שלך התקבלה. אנו נשלח אלייך הודעת וואטסאפ לאישור סופי.'
  );
  const [savingMessage, setSavingMessage] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [exceptions, setExceptions] = useState<Array<{ id: string; date: string; closed: boolean; note: string | null }>>([]);
  const [newExDate, setNewExDate] = useState('');
  const [newExNote, setNewExNote] = useState('');
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editWeekdayGroup, setEditWeekdayGroup] = useState(false);
  const [savingDay, setSavingDay] = useState(false);
  const [savingRules, setSavingRules] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('clinic_schedule').select('*').limit(1).maybeSingle();
      if (data) {
        setId(data.id);
        const w = data.weekly as unknown as Day[];
        setWeekly(w);
        setMode(detectMode(w));
        setBuffer(data.buffer_minutes);
        setSlotStep(data.slot_step_minutes);
        setMinLead(data.min_lead_minutes);
        setHorizon(data.horizon_days);
        if ((data as any).confirmation_message) setConfirmationMessage((data as any).confirmation_message);
      }
      const { data: ex } = await supabase.from('schedule_exceptions').select('id,date,closed,note').order('date');
      setExceptions((ex as any) ?? []);
      setLoaded(true);
    })();
  }, []);

  const updateDay = (i: number, next: Day) => {
    setWeekly((w) => w.map((d, idx) => (idx === i ? next : d)));
  };

  const updateWeekdayGroup = (next: Day) => {
    setWeekly((w) => w.map((d, idx) => (WEEKDAY_INDICES.includes(idx) ? next : d)));
  };

  const persistSchedule = async () => {
    if (!id) return;
    const { error } = await supabase
      .from('clinic_schedule')
      .update({
        weekly: weekly as unknown as never,
        buffer_minutes: buffer,
        slot_step_minutes: slotStep,
        min_lead_minutes: minLead,
        horizon_days: horizon,
        confirmation_message: confirmationMessage,
      } as never)
      .eq('id', id);
    if (error) throw new Error(error.message);
  };

  const saveMessage = async () => {
    setSavingMessage(true);
    try {
      await persistSchedule();
      toast.success('הודעת אישור נשמרה');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSavingMessage(false);
    }
  };

  const saveDay = async () => {
    setSavingDay(true);
    try {
      await persistSchedule();
      toast.success('שעות נשמרו');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSavingDay(false);
    }
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      await persistSchedule();
      toast.success('כללי הזמנה נשמרו');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSavingRules(false);
    }
  };

  const addException = async () => {
    if (!newExDate) return;
    const { data, error } = await supabase
      .from('schedule_exceptions')
      .insert({ date: newExDate, closed: true, note: newExNote || null })
      .select('id,date,closed,note')
      .single();
    if (error) return toast.error(error.message);
    setExceptions((arr) => [...arr, data as any].sort((a, b) => a.date.localeCompare(b.date)));
    setNewExDate('');
    setNewExNote('');
    toast.success('חריג נוסף');
  };

  const removeException = async (exId: string) => {
    await supabase.from('schedule_exceptions').delete().eq('id', exId);
    setExceptions((arr) => arr.filter((e) => e.id !== exId));
  };

  const weekdayRepresentative = weekly[0] ?? emptyDay();
  const friday = weekly[FRIDAY] ?? closedDay();
  const saturday = weekly[SATURDAY] ?? closedDay();

  const openEdit = (i: number) => {
    setEditIndex(i);
    setEditWeekdayGroup(false);
  };
  const openEditWeekdayGroup = () => {
    setEditIndex(0);
    setEditWeekdayGroup(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">הגדרות יומן</h1>
        <p className="text-sm text-muted-foreground">
          שעות עבודה גלובליות. יש ללחוץ "שמירה" בכל סקציה לאחר עריכה. ניתן לעקוף ברמת טיפול ב"חוקי שירותים".
        </p>
      </div>

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-medium">מצב תצוגה</h2>
          <div className="inline-flex rounded-md border p-0.5 text-sm">
            <button
              onClick={() => setMode('unified')}
              className={`px-3 py-1 rounded ${mode === 'unified' ? 'bg-neutral-900 text-white' : ''}`}
            >
              גלובלי אחיד
            </button>
            <button
              onClick={() => setMode('per-day')}
              className={`px-3 py-1 rounded ${mode === 'per-day' ? 'bg-neutral-900 text-white' : ''}`}
            >
              פר־יום
            </button>
          </div>
        </div>

        {mode === 'unified' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <DayTile
              title="ימי חול א׳–ה׳"
              day={weekdayRepresentative}
              expanded={editWeekdayGroup}
              onToggle={() =>
                editWeekdayGroup ? setEditIndex(null) : openEditWeekdayGroup()
              }
              onChange={updateWeekdayGroup}
              tone="primary"
            />
            <DayTile
              title="שישי"
              day={friday}
              expanded={!editWeekdayGroup && editIndex === FRIDAY}
              onToggle={() =>
                editIndex === FRIDAY && !editWeekdayGroup
                  ? setEditIndex(null)
                  : openEdit(FRIDAY)
              }
              onChange={(d) => updateDay(FRIDAY, d)}
              tone="weekend"
            />
            <DayTile
              title="שבת"
              day={saturday}
              expanded={!editWeekdayGroup && editIndex === SATURDAY}
              onToggle={() =>
                editIndex === SATURDAY && !editWeekdayGroup
                  ? setEditIndex(null)
                  : openEdit(SATURDAY)
              }
              onChange={(d) => updateDay(SATURDAY, d)}
              tone="weekend"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {weekly.map((day, i) => (
              <DayTile
                key={i}
                title={DAY_NAMES[i]}
                day={day}
                expanded={!editWeekdayGroup && editIndex === i}
                onToggle={() =>
                  editIndex === i && !editWeekdayGroup
                    ? setEditIndex(null)
                    : openEdit(i)
                }
                onChange={(d) => updateDay(i, d)}
                tone={i === SATURDAY || i === FRIDAY ? 'weekend' : 'default'}
              />
            ))}
          </div>
        )}
        <div className="flex justify-start pt-2 border-t">
          <Button onClick={saveDay} disabled={savingDay || !loaded}>
            {savingDay ? 'שומר…' : 'שמירת שעות'}
          </Button>
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <h2 className="font-medium">כללי הזמנה</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <NumField label="מרווח בין טיפולים (דק׳)" value={buffer} onChange={setBuffer} />
          <NumField label="צעד סלוט (דק׳)" value={slotStep} onChange={setSlotStep} />
          <NumField label="זמן התראה מינ׳ (דק׳)" value={minLead} onChange={setMinLead} />
          <NumField label="טווח הזמנות (ימים)" value={horizon} onChange={setHorizon} />
        </div>
        <div className="flex justify-start pt-2 border-t">
          <Button onClick={saveRules} disabled={savingRules || !loaded}>
            {savingRules ? 'שומר…' : 'שמירת כללי הזמנה'}
          </Button>
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div>
          <h2 className="font-medium">הודעת אישור (מסך סיום הזמנה)</h2>
          <p className="text-xs text-muted-foreground mt-1">
            הטקסט שמופיע ללקוחה לאחר שליחת הבקשה. ניתן לפצל לשורות.
          </p>
        </div>
        <Textarea
          value={confirmationMessage}
          onChange={(e) => setConfirmationMessage(e.target.value)}
          rows={4}
          dir="rtl"
          placeholder="הבקשה שלך התקבלה…"
        />
        <div className="flex justify-start pt-2 border-t">
          <Button onClick={saveMessage} disabled={savingMessage || !loaded}>
            {savingMessage ? 'שומר…' : 'שמירת הודעה'}
          </Button>
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <h2 className="font-medium flex items-center gap-2">
          <CalendarIcon className="h-4 w-4" /> חריגים בתאריכים
        </h2>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <Label className="text-xs">תאריך</Label>
            <Input type="date" value={newExDate} onChange={(e) => setNewExDate(e.target.value)} />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">הערה</Label>
            <Input
              value={newExNote}
              onChange={(e) => setNewExNote(e.target.value)}
              placeholder="חג, יום חופש…"
            />
          </div>
          <Button onClick={addException}>
            <Plus className="h-4 w-4 ms-1" /> הוספת סגירה
          </Button>
        </div>
        <div className="divide-y border rounded-md">
          {exceptions.length === 0 && (
            <div className="p-3 text-sm text-muted-foreground">אין חריגים.</div>
          )}
          {exceptions.map((e) => (
            <div key={e.id} className="flex items-center justify-between p-3 text-sm">
              <div>
                <span className="font-medium">{e.date}</span>{' '}
                {e.closed ? '— סגור' : '— מותאם'} {e.note ? `· ${e.note}` : ''}
              </div>
              <Button size="sm" variant="ghost" onClick={() => removeException(e.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </Card>

    </div>
  );
}

function DayTile({
  title,
  day,
  expanded,
  onToggle,
  onChange,
  tone,
}: {
  title: string;
  day: Day;
  expanded: boolean;
  onToggle: () => void;
  onChange: (d: Day) => void;
  tone: 'default' | 'primary' | 'weekend';
}) {
  const toneClass =
    tone === 'primary'
      ? 'border-neutral-900/20 bg-neutral-900/[0.03]'
      : tone === 'weekend'
      ? 'border-amber-200 bg-amber-50/40'
      : 'border-neutral-200 bg-white';

  return (
    <div className={`rounded-lg border ${toneClass} p-4 flex flex-col min-h-[160px]`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-medium">{title}</div>
        {day.enabled ? (
          <Badge variant="secondary" className="text-[10px]">פתוח</Badge>
        ) : (
          <Badge variant="outline" className="text-[10px]">סגור</Badge>
        )}
      </div>
      <div className="flex-1 space-y-1.5 text-sm">
        {!day.enabled && <div className="text-xs text-muted-foreground">היום סגור.</div>}
        {day.enabled && day.intervals.length === 0 && (
          <div className="text-xs text-muted-foreground">לא הוגדרו שעות.</div>
        )}
        {day.enabled &&
          day.intervals.map((iv, i) => (
            <div key={`w${i}`} className="flex items-center gap-1.5 text-[13px]">
              <Clock className="h-3.5 w-3.5 text-emerald-600" />
              <span className="tabular-nums">
                {iv.start}–{iv.end}
              </span>
            </div>
          ))}
        {day.enabled &&
          day.breaks.map((iv, i) => (
            <div key={`b${i}`} className="flex items-center gap-1.5 text-[13px] text-amber-700">
              <Coffee className="h-3.5 w-3.5" />
              <span className="tabular-nums">
                {iv.start}–{iv.end}
              </span>
              <span className="text-[11px] text-muted-foreground">הפסקה</span>
            </div>
          ))}
      </div>
      <Button size="sm" variant="outline" className="mt-3 w-full" onClick={onToggle}>
        {expanded ? (
          <>
            <ChevronUp className="h-3.5 w-3.5 me-1" /> סגירה
          </>
        ) : (
          <>
            <Pencil className="h-3.5 w-3.5 me-1" /> עריכה
          </>
        )}
      </Button>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-neutral-200/60">
          <DayEditor day={day} onChange={onChange} />
        </div>
      )}
    </div>
  );
}


type DayKind = 'working' | 'break';
type KindedRow = { kind: DayKind; start: string; end: string };

const KIND_LABEL: Record<DayKind, string> = {
  working: 'שעות עבודה',
  break: 'הפסקה',
};

const KIND_STYLE: Record<DayKind, string> = {
  working: 'bg-emerald-50 border-emerald-200',
  break: 'bg-amber-50 border-amber-200',
};

function dayToRows(day: Day): KindedRow[] {
  return [
    ...day.intervals.map((iv) => ({ kind: 'working' as DayKind, ...iv })),
    ...day.breaks.map((iv) => ({ kind: 'break' as DayKind, ...iv })),
  ];
}

function rowsToDay(rows: KindedRow[], enabled: boolean): Day {
  return {
    enabled,
    intervals: rows.filter((r) => r.kind === 'working').map(({ start, end }) => ({ start, end })),
    breaks: rows.filter((r) => r.kind === 'break').map(({ start, end }) => ({ start, end })),
  };
}

function DayEditor({ day, onChange }: { day: Day; onChange: (d: Day) => void }) {
  const rows = dayToRows(day);
  const updateRows = (next: KindedRow[]) => onChange(rowsToDay(next, day.enabled));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm">פתוח להזמנות</Label>
        <Switch
          checked={day.enabled}
          onCheckedChange={(v) =>
            onChange({
              ...day,
              enabled: v,
              intervals:
                v && day.intervals.length === 0
                  ? [{ start: '09:00', end: '17:00' }]
                  : day.intervals,
            })
          }
        />
      </div>
      {day.enabled && (
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground">
            טווחי שעות — שעות עבודה והפסקות. ימים ריקים = ללא הגבלה.
          </div>
          {rows.length === 0 && (
            <div className="text-xs text-muted-foreground">—</div>
          )}
          {rows.map((row, i) => (
            <div
              key={i}
              className={`flex flex-wrap items-center gap-2 rounded border px-2 py-1.5 ${KIND_STYLE[row.kind]}`}
            >
              <select
                value={row.kind}
                onChange={(e) =>
                  updateRows(
                    rows.map((r, idx) =>
                      idx === i ? { ...r, kind: e.target.value as DayKind } : r,
                    ),
                  )
                }
                className="h-8 rounded border bg-white px-2 text-xs"
              >
                {(Object.keys(KIND_LABEL) as DayKind[]).map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABEL[k]}
                  </option>
                ))}
              </select>
              <Input
                type="time"
                value={row.start}
                className="w-28 h-8"
                onChange={(e) =>
                  updateRows(
                    rows.map((r, idx) => (idx === i ? { ...r, start: e.target.value } : r)),
                  )
                }
              />
              <span>–</span>
              <Input
                type="time"
                value={row.end}
                className="w-28 h-8"
                onChange={(e) =>
                  updateRows(
                    rows.map((r, idx) => (idx === i ? { ...r, end: e.target.value } : r)),
                  )
                }
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => updateRows(rows.filter((_, idx) => idx !== i))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                updateRows([...rows, { kind: 'working', start: '09:00', end: '17:00' }])
              }
            >
              <Plus className="h-4 w-4 me-1" /> שעות עבודה
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                updateRows([...rows, { kind: 'break', start: '13:00', end: '14:00' }])
              }
            >
              <Plus className="h-4 w-4 me-1" /> הפסקה
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function NumField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type="number" value={value} onChange={(e) => onChange(Number(e.target.value) || 0)} />
    </div>
  );
}
